import { z } from 'zod';
import type { FormField } from '@@/shared/types/schema';

/**
 * Traduce i messaggi di errore di uno schema Zod in italiano
 * Ricrea lo schema con i messaggi tradotti basandosi sui campi
 */
export const translateZodSchema = (
	schema: z.ZodObject<any>,
	fields: FormField[],
): z.ZodObject<any> => {
	const shape: Record<string, z.ZodTypeAny> = {};
	const fieldMap = new Map(fields.map((f) => [f.name, f]));

	// Itera su tutti i campi dello schema
	Object.keys(schema.shape).forEach((fieldName) => {
		const field = fieldMap.get(fieldName);
		const fieldSchema = schema.shape[fieldName];
		const fieldLabel = field?.label || fieldName;

		// Gestisci prima radio e select (devono essere gestiti prima di text/textarea)
		if (field?.type === 'radio' || field?.type === 'select') {
			if (field.required) {
				// Per campi obbligatori, la stringa non può essere vuota
				shape[fieldName] = z.string().min(1, `${fieldLabel} è obbligatorio`);
			} else {
				shape[fieldName] = z.string().optional().or(z.literal(''));
			}
		} else if (field?.type === 'checkbox_group') {
			// Gestisci checkbox_group
			let translatedSchema = z.array(z.string());
			if (field.required) {
				// Per campi obbligatori, l'array deve avere almeno un elemento
				translatedSchema = translatedSchema.refine((val) => Array.isArray(val) && val.length > 0, {
					message: `${fieldLabel} è obbligatorio`,
				});
			} else {
				translatedSchema = translatedSchema.optional().or(z.literal([]));
			}
			shape[fieldName] = translatedSchema;
		} else if (field?.type === 'checkbox') {
			if (field.required) {
				shape[fieldName] = z
					.boolean()
					.refine((val) => val === true, {
						message: `${fieldLabel} è obbligatorio`,
					});
			} else {
				shape[fieldName] = z.boolean().default(false);
			}
		} else if (field?.type === 'file') {
			if (field.required) {
				shape[fieldName] = z.instanceof(File, {
					message: `${fieldLabel} è obbligatorio`,
				});
			} else {
				shape[fieldName] = z.instanceof(File, {
					message: `${fieldLabel} deve essere un file valido se fornito`,
				}).or(z.undefined());
			}
		} else if (fieldSchema instanceof z.ZodString || field?.type === 'text' || field?.type === 'textarea') {
			// Ricrea lo schema stringa con validazioni tradotte
			let translatedSchema = z.string();

			// Applica le regole di validazione se presenti
			if (field?.validation) {
				const rules = field.validation.split('|');
				rules.forEach((rule) => {
					const [ruleName, ruleValue] = rule.split(':');
					const normalizedRule = ruleName?.toLowerCase();

					switch (normalizedRule) {
						case 'email':
							translatedSchema = translatedSchema.email(`${fieldLabel} deve essere un'email valida`);
							break;
						case 'url':
							translatedSchema = translatedSchema.url(`${fieldLabel} deve essere un URL valido`);
							break;
						case 'min': {
							const min = ruleValue ? parseInt(ruleValue, 10) : 0;
							translatedSchema = translatedSchema.min(min, `${fieldLabel} deve contenere almeno ${min} caratteri`);
							break;
						}
						case 'max': {
							const max = ruleValue ? parseInt(ruleValue, 10) : Infinity;
							translatedSchema = translatedSchema.max(max, `${fieldLabel} deve contenere al massimo ${max} caratteri`);
							break;
						}
						case 'length': {
							const length = ruleValue ? parseInt(ruleValue, 10) : 0;
							translatedSchema = translatedSchema.length(length, `${fieldLabel} deve contenere esattamente ${length} caratteri`);
							break;
						}
					}
				});
			}

			// Se è obbligatorio, aggiungi la validazione
			if (field?.required) {
				translatedSchema = translatedSchema.min(1, `${fieldLabel} è obbligatorio`);
			} else {
				translatedSchema = translatedSchema.or(z.literal('')).or(z.undefined());
			}

			shape[fieldName] = translatedSchema;
		} else {
			// Per altri tipi (array, union, etc.), mantieni lo schema originale
			shape[fieldName] = fieldSchema;
		}
	});

	return z.object(shape);
};

